import { Fragment, useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Grid, Paper, Typography, Avatar, TextField, IconButton, CircularProgress, Badge, Divider,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SendIcon from '@mui/icons-material/Send';
import ChatBubbleOutlineOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineOutlined';
import { conectarSocketAdmin } from '../../utils/socket';
import { getSoporteChats, getSoporteChat, responderSoporte, marcarSoporteLeido } from '../../actions/support';
import { urlImg, isEmpty } from '../../components/common/helpers';
import BotonAdjuntar from '../../components/chat/BotonAdjuntar';
import VistaPreviaAdjuntos from '../../components/chat/VistaPreviaAdjuntos';
import AdjuntosMensaje from '../../components/chat/AdjuntosMensaje';
import { useAdjuntos } from '../../components/chat/useAdjuntos';
import { MAX_TEXTO_SOPORTE_ADMIN } from '../../components/chat/limites';
import { etiquetaDia, horaMensaje, mismoDia } from '../../components/chat/fechas';

const avatarUrl = (u) => {
  if (!u || isEmpty(u.avatar)) return '';
  const a = u.avatar.toString();
  if (a.startsWith('http')) return a;
  if (a.startsWith('//')) return a.replace(/^\/\//, 'https://');
  const v = u.versionAvatar ? `v${u.versionAvatar}/` : '';
  return `${urlImg}/w_80,h_80,c_fill/${v}users/${u._id}/${a}`;
};

const nombreDe = (u) => `${u?.name || ''} ${u?.apellido || ''}`.trim() || 'Usuario';
const inicial = (u) => (u?.name || '?').charAt(0).toUpperCase();

const horaCorta = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return 'ahora';
  const m = Math.floor(s / 60); if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h`;
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit' });
};

const Burbuja = ({ msg }) => {
  const admin = msg.from === 'admin'; // mensaje del superadmin (yo) -> derecha
  return (
    <Box sx={{ display: 'flex', justifyContent: admin ? 'flex-end' : 'flex-start', mb: 1.1 }}>
      <Box sx={{
        maxWidth: '76%', px: 1.6, py: 1, borderRadius: 2.5,
        bgcolor: admin ? 'primary.main' : 'sutil',
        color: admin ? 'white' : 'primary.main',
        borderTopRightRadius: admin ? 4 : 20,
        borderTopLeftRadius: admin ? 20 : 4,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
      }}>
        {!msg.soloAdjuntos && <Typography variant="body2">{msg.content}</Typography>}
        <AdjuntosMensaje adjuntos={msg.adjuntos} />
        <Typography variant="caption" sx={{ display: 'block', mt: 0.3, opacity: 0.7, textAlign: 'right', fontSize: '0.65rem' }}>
          {horaMensaje(msg.createdAt)}
        </Typography>
      </Box>
    </Box>
  );
};

const SoporteAdmin = () => {
  const [chats, setChats] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(true);

  const [activo, setActivo] = useState(null);       // userId abierto
  const [userActivo, setUserActivo] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [cargandoChat, setCargandoChat] = useState(false);

  const [texto, setTexto] = useState('');
  const [enviando, setEnviando] = useState(false);
  const adj = useAdjuntos();

  const activoRef = useRef(null);
  const finRef = useRef(null);
  const socketRef = useRef(null);

  useEffect(() => { activoRef.current = activo; }, [activo]);

  const scrollAbajo = () => {
    requestAnimationFrame(() => finRef.current?.scrollIntoView({ behavior: 'smooth' }));
  };

  const cargarLista = useCallback(async () => {
    const r = await getSoporteChats();
    if (r.ok) setChats(r.chats);
    setCargandoLista(false);
  }, []);

  useEffect(() => { cargarLista(); }, [cargarLista]);

  // Socket: mensajes nuevos de los usuarios (sala 'admins')
  useEffect(() => {
    const socket = conectarSocketAdmin();
    socketRef.current = socket;
    socket.on('connect', () => socket.emit('join_admin'));

    socket.on('support_message', (data) => {
      if (!data || !data.userId) return;
      const abierto = String(activoRef.current) === String(data.userId);

      setChats((prev) => {
        const existing = prev.find((c) => String(c.user?._id) === String(data.userId));
        const others = prev.filter((c) => String(c.user?._id) !== String(data.userId));
        const actualizado = {
          _id: data.chatId,
          user: data.user || existing?.user,
          lastMessagePreview: data.lastMessagePreview || data.message?.content || '',
          lastMessageAt: data.lastMessageAt || new Date().toISOString(),
          unreadAdmin: abierto ? 0 : (data.unreadAdmin ?? ((existing?.unreadAdmin || 0) + 1)),
          totalMensajes: (existing?.totalMensajes || 0) + 1,
        };
        return [actualizado, ...others];
      });

      if (abierto && data.message) {
        setMensajes((prev) => [...prev, data.message]);
        scrollAbajo();
        marcarSoporteLeido(data.userId); // ya lo está viendo: que el badge del menú no lo cuente
      }
    });

    return () => { socket.disconnect(); socketRef.current = null; };
  }, []);

  const abrir = async (userId) => {
    adj.limpiar(); // los archivos elegidos eran de la conversación anterior: no deben irse a otra persona
    setActivo(userId);
    setCargandoChat(true);
    setMensajes([]);
    setChats((prev) => prev.map((c) => (String(c.user?._id) === String(userId) ? { ...c, unreadAdmin: 0 } : c)));
    const r = await getSoporteChat(userId);
    setCargandoChat(false);
    if (r.ok) {
      setMensajes(r.chat.messages || []);
      setUserActivo(r.chat.user);
      scrollAbajo();
    }
  };

  const responder = async () => {
    const content = texto.trim();
    const archivos = adj.archivos;
    if ((!content && archivos.length === 0) || enviando || adj.procesando || !activo) return;
    setEnviando(true);
    setTexto('');
    const r = await responderSoporte(activo, content, archivos);
    setEnviando(false);
    if (r.ok) {
      adj.limpiar();
      setMensajes((prev) => [...prev, r.message]);
      setChats((prev) => prev.map((c) => (String(c.user?._id) === String(activo)
        ? { ...c, lastMessagePreview: (r.message?.content || content).slice(0, 120), lastMessageAt: new Date().toISOString() }
        : c)));
      scrollAbajo();
    } else {
      setTexto((actual) => actual || content); // no se envió: devolvemos el texto y conservamos los archivos
      adj.setError(r.error);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); responder(); }
  };

  // Referencia para "Hoy" / "Ayer" en los separadores de la conversación
  const ahora = new Date();

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} color="primary.main" sx={{ mb: 0.5 }}>Chat en vivo</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Conversaciones de soporte con clientes y proveedores.
      </Typography>

      <Grid container spacing={2}>
        {/* Lista de conversaciones */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper elevation={0} sx={{ border: 1, borderColor: 'sutil', borderRadius: 3, height: { md: '68vh' }, display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'sutil' }}>
              <Typography variant="subtitle2" fontWeight={700} color="primary.main">Conversaciones</Typography>
            </Box>
            <Box sx={{ flex: 1, overflowY: 'auto' }}>
              {cargandoLista ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={22} sx={{ color: 'secondary.main' }} /></Box>
              ) : chats.length === 0 ? (
                <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6, px: 2 }}>
                  <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 34, mb: 1 }} />
                  <Typography variant="body2">Aún no hay conversaciones.</Typography>
                </Box>
              ) : chats.map((c) => {
                const sel = String(activo) === String(c.user?._id);
                return (
                  <Box
                    key={c._id || c.user?._id}
                    onClick={() => abrir(c.user?._id)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.5, px: 2, py: 1.4, cursor: 'pointer',
                      borderBottom: 1, borderColor: 'grey.50',
                      bgcolor: sel ? ((t) => alpha(t.palette.secondary.main, 0.12)) : 'transparent', '&:hover': { bgcolor: sel ? ((t) => alpha(t.palette.secondary.main, 0.12)) : 'grey.50' },
                    }}
                  >
                    <Badge color="error" badgeContent={c.unreadAdmin || 0} overlap="circular">
                      <Avatar src={avatarUrl(c.user)} sx={{ bgcolor: 'secondary.main', color: 'primary.main' }}>{inicial(c.user)}</Avatar>
                    </Badge>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                        <Typography variant="body2" fontWeight={700} color="primary.main" noWrap>{nombreDe(c.user)}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>{horaCorta(c.lastMessageAt)}</Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                        {c.lastMessagePreview || 'Sin mensajes'}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </Paper>
        </Grid>

        {/* Conversación */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper elevation={0} sx={{ border: 1, borderColor: 'sutil', borderRadius: 3, height: { xs: '60vh', md: '68vh' }, display: 'flex', flexDirection: 'column' }}>
            {!activo ? (
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'text.secondary', px: 3, textAlign: 'center' }}>
                <ChatBubbleOutlineOutlinedIcon sx={{ fontSize: 44, mb: 1 }} />
                <Typography variant="body2">Elige una conversación para responder.</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ px: 2, py: 1.4, borderBottom: 1, borderColor: 'sutil', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar src={avatarUrl(userActivo)} sx={{ width: 36, height: 36, bgcolor: 'secondary.main', color: 'primary.main' }}>{inicial(userActivo)}</Avatar>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="subtitle2" fontWeight={700} color="primary.main" noWrap>{nombreDe(userActivo)}</Typography>
                    {!!userActivo?.email && <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{userActivo.email}</Typography>}
                  </Box>
                </Box>

                <Box sx={{ flex: 1, overflowY: 'auto', p: 2 }}>
                  {cargandoChat ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}><CircularProgress size={22} sx={{ color: 'secondary.main' }} /></Box>
                  ) : mensajes.length === 0 ? (
                    <Box sx={{ textAlign: 'center', color: 'text.secondary', py: 6 }}>
                      <Typography variant="body2">Sin mensajes todavía.</Typography>
                    </Box>
                  ) : (
                    mensajes.map((m, i) => {
                      // El primer mensaje de cada día lleva arriba el día en que se mandó ("Hoy", "Ayer", "9 de octubre"…)
                      const dia = i === 0 || !mismoDia(mensajes[i - 1].createdAt, m.createdAt) ? etiquetaDia(m.createdAt, ahora) : '';
                      return (
                        <Fragment key={m._id || i}>
                          {dia && (
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: i === 0 ? 0 : 2, mb: 1.5 }}>
                              <Divider sx={{ flex: 1 }} />
                              <Typography variant="caption" sx={{ px: 1.5, color: 'text.secondary', fontWeight: 600 }}>{dia}</Typography>
                              <Divider sx={{ flex: 1 }} />
                            </Box>
                          )}
                          <Burbuja msg={m} />
                        </Fragment>
                      );
                    })
                  )}
                  <div ref={finRef} />
                </Box>

                <Box sx={{ borderTop: 1, borderColor: 'sutil', p: 1.5 }}>
                  <VistaPreviaAdjuntos archivos={adj.archivos} error={adj.error} onQuitar={adj.quitar} enviando={enviando} procesando={adj.procesando} />
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
                    <BotonAdjuntar onArchivos={adj.agregar} disabled={enviando} />
                    <TextField
                      fullWidth size="small" multiline maxRows={4}
                      placeholder="Escribe tu respuesta…"
                      value={texto}
                      onChange={(e) => setTexto(e.target.value)}
                      onKeyDown={onKeyDown}
                      inputProps={{ maxLength: MAX_TEXTO_SOPORTE_ADMIN }}
                    />
                    <IconButton
                      onClick={responder}
                      aria-label="Enviar respuesta"
                      disabled={(!texto.trim() && adj.archivos.length === 0) || enviando || adj.procesando}
                      sx={{ bgcolor: 'primary.main', color: 'white', '&:hover': { bgcolor: 'primary.dark' }, '&.Mui-disabled': { bgcolor: 'grey.400', color: 'white' } }}
                    >
                      {enviando ? <CircularProgress size={18} color="inherit" /> : <SendIcon fontSize="small" />}
                    </IconButton>
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SoporteAdmin;
